<template>
    <v-layout class="fill-height">
        <v-container class="pt-16" v-if="!isLoading">
            <v-row>
                <v-col cols="12">
                    <v-card class="grey lighten-4 mb-8">
                        <v-card-title>
                            <v-btn :to="{name: 'Bookings'}" x-small text class="mr-4">
                                <v-icon left>
                                    mdi-arrow-left
                                </v-icon>
                                Back to Overview
                            </v-btn>
                            Summary of {{getTypeLabel(booking.type)}}
                        </v-card-title>
                        <v-card-text>
                            <v-row>
                                <v-col cols="2">
                                    <dl>
                                        <dt>ASI Tourcode</dt>
                                        <dd>{{booking.travelCode}}</dd>
                                    </dl>
                                </v-col>
                                <v-col cols="2">
                                    <dl>
                                        <dt>Tourcode</dt>
                                        <dd>{{booking.extCode}}</dd>
                                    </dl>
                                </v-col>
                                <v-col cols="2">
                                    <dl>
                                        <dt>Type</dt>
                                        <dd>{{getTypeLabel(booking.type)}}</dd>
                                    </dl>
                                </v-col>
                                <v-col cols="1">
                                    <dl>
                                        <dt>Received</dt>
                                        <dd>{{new Date(booking.createdAt).toLocaleDateString()}}</dd>
                                    </dl>
                                </v-col>
                                <v-col cols="3">
                                    <dl>
                                        <dt>Tourname</dt>
                                        <dd>{{booking.tripName}}</dd>
                                    </dl>
                                </v-col>
                                <v-col cols="2">
                                    <dl>
                                        <dt>Total Price</dt>
                                        <dd>
                                            <span v-if="!booking.offerPrice">{{bookingPrice | price}}</span>
                                            <span v-else style="display:inline-flex;align-items:baseline">
                                                <span>{{(bookingPrice + booking.offerPrice) | price}}</span>
                                                <v-tooltip bottom>
                                                    <template v-slot:activator="{on, attrs}">
                                                        <v-icon small v-bind="attrs" v-on="on"
                                                                class="pl-2 orange--text">mdi-alert</v-icon>
                                                    </template>
                                                    <span>Old price was {{bookingPrice | price}}</span>
                                                </v-tooltip>
                                            </span>
                                        </dd>
                                    </dl>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="2">
                                    <dl>
                                        <dt>Arrival</dt>
                                        <dd>
                                            <span v-if="!booking.offerDate">{{new Date(booking.tourStart).toLocaleDateString()}}</span>
                                            <span v-else style="display:inline-flex;align-items:baseline">
                                                <span>{{new Date(booking.offerDate).toLocaleDateString()}}</span>
                                                <v-tooltip bottom>
                                                    <template v-slot:activator="{on, attrs}">
                                                        <v-icon small v-bind="attrs" v-on="on"
                                                                class="pl-2 orange--text">mdi-alert</v-icon>
                                                    </template>
                                                    <span>Old departure date was {{new Date(booking.tourStart).toLocaleDateString()}}</span>
                                                </v-tooltip>
                                            </span>
                                        </dd>
                                    </dl>
                                </v-col>
                                <v-col cols="2">
                                    <dl>
                                        <dt
                                            v-if="booking.pax > 1"
                                        >
                                            Guests
                                        </dt>
                                        <dt
                                            v-else
                                        >
                                            Guest
                                        </dt>
                                        <dd>{{booking.pax}}</dd>
                                    </dl>
                                </v-col>
                                <v-col cols="2">
                                    <dl>
                                        <dt>Room Type</dt>
                                        <dd>
                                            <room-type :room-types="booking.roomTypes"></room-type>
                                        </dd>
                                    </dl>
                                </v-col>
                                <v-col
                                    cols="2"
                                    v-if="booking.bikes > 0"
                                >
                                    <dl>
                                        <dt>Bikes</dt>
                                        <dd>{{booking.bikes}}</dd>
                                    </dl>
                                </v-col>
                                <v-col
                                    cols="4"
                                    v-if="booking.erpId"
                                >
                                    <dl>
                                        <dt>ASI Booking Reference</dt>
                                        <dd>{{booking.erpId}}</dd>
                                    </dl>
                                </v-col>
                                <v-col
                                    cols="1"
                                    v-if="parseInt(booking.status)===0"
                                >
                                    <dl>
                                        <dt>Time to Respond</dt>
                                        <dd>
                                            <v-chip color="orange">
                                                <v-icon left>
                                                    mdi-timer-outline
                                                </v-icon>
                                                <expiry-countdown :booking="booking"/>
                                            </v-chip>
                                        </dd>
                                    </dl>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="12">
                                    <dl>
                                        <dt>{{$t('label.contact')}}</dt>
                                        <dd>{{booking.user.firstname}} {{booking.user.lastname}} </dd>
                                        <dd>{{booking.user.telephone}}</dd>
                                    </dl>
                                </v-col>
                            </v-row>
                            <v-row v-if="booking.comment">
                                <v-col cols="12">
                                    <dl>
                                        <dt>Internal Comment</dt>
                                        <dd>{{booking.comment}}</dd>
                                    </dl>
                                </v-col>
                            </v-row>
                        </v-card-text>
                    </v-card>
                    <room
                        v-for="(room, key) in booking.rooms"
                        :key="key"
                        :room="room"
                        :booking="booking"
                    >
                    </room>
                </v-col>
            </v-row>
            <v-row
                v-if="actionsAvailable"
            >
                <v-col cols="12">
                    <actions></actions>
                </v-col>
            </v-row>
        </v-container>
        <v-container class="pt-16" v-else>
            <v-row>
                <v-col cols="12">
                    <v-skeleton-loader type="image" height="208px"></v-skeleton-loader>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12">
                    <v-skeleton-loader type="image" height="48px"></v-skeleton-loader>
                </v-col>
            </v-row>
        </v-container>
    </v-layout>
</template>

<script>
    import '../filter/price';
    import {mapActions, mapGetters} from "vuex";
    import {BookingsActions} from "../store/bookings/types";
    import RoomType from "../components/List/RoomType";
    import {getBikes} from "../utils/bike";
    import Room from "../components/Booking/Room";
    import Actions from "../components/Booking/Actions";
    import ExpiryCountdown from "../components/Booking/ExpiryCountdown";

    export default {
        components: {
            ExpiryCountdown,
            RoomType,
            Room,
            Actions,
        },
        created() {
            this.load(this.$route.params.id);
        },
        computed: {
            ...mapGetters({
                booking: 'bookings/booking',
                loading: 'bookings/isLoading',
            }),
            isLoading() {
                return this.loading || this.booking === null;
            },
            bikes() {
                return getBikes(this.booking);
            },
            actionsAvailable() {
                return this.booking.status === 0 && this.expired === false;
            },
            expired(){
                return new Date(this.booking.schedule.start) <= new Date() || this.booking.status === 4;
            },
            bookingPrice(){
                return this.booking.priceTotal - this.booking.flexPrice;
            }
        },
        methods: {
            ...mapActions({
                load: `bookings/${BookingsActions.LOAD_DETAIL}`,
            }),
            getTypeLabel: (type) => {
                switch (type) {
                    case 'B':
                        return 'Booking';
                    case 'OA':
                        return 'Booking Enquiry';
                    case 'W':
                        return 'Enquiry';
                }

                return '';
            },
        },
    }
</script>

<style lang="scss">
    dt {
        font-weight: bold;
        margin-bottom: 8px;
    }
</style>
